import fetchQuery from 'rebook/apps/utils/fetchQuery';
import * as SeoActions from 'rebook/apps/base/actions/SeoActions';
import * as Error404Actions from 'rebook/apps/base/actions/Error404Actions';
import {
  BOOKS_BOOK_FRAGMENT_SET_DATA,
  BOOKS_BOOK_FRAGMENT_RESET_DATA,
} from 'rebook/apps/books/constants';
import truncateChars from 'rebook/apps/utils/text/truncateChars';
import cropStorageImage from 'rebook/apps/utils/urls/cropStorageImage';
import getPageNumberString from 'rebook/apps/utils/text/getPageNumberString';
import { Book } from 'rebook/apps/books/models';


export function fetchInitialData(match, location = {}) {
  return async(dispatch, getState) => {
    const currentState = getState().bookFragment;
    const ssr = getState().ssr;
    const { bookSlug } = match.params;
    const pageNumber = ssr.query && ssr.query.page
      ? parseInt(ssr.query.page, 10)
      : 1;
    let newState = {
      bookSlug,
      pageNumber,
    };
    if (location && location.state) {
      newState = {
        ...newState,
        ...location.state,
      };
    }
    if (currentState.fragment) {
      newState = {
        ...newState,
        ...currentState,
      };
    }

    // set partial state (it could probably be the data obtained from router)
    dispatch(setData(newState));

    if (!newState.book) {
      await dispatch(fetchBook(match));
    }
    if (!newState.fragment || currentState.pageNumber !== pageNumber) {
      await dispatch(fetchFragmentPage(pageNumber));
    }
  };
}

export function fetchBook(match) {
  return async(dispatch, getState) => {
    const { ssr } = getState();
    const { bookSlug } = match.params;
    const url = `/api/books/s-${bookSlug}/`;
    return await fetchQuery(url, { apiVersion: 4, headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({ book: data }));
        }
      )
      .catch(
        ({ data, resp, error }) => {
          if (resp.status === 404) {
            dispatch(Error404Actions.show());
          }
          else {
            Raven.captureMessage('failed to load fragment book due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load fragment book detail due to server error');
          }
        }
      );
  };
}

export function fetchFragmentPage(page=1) {
  return async(dispatch, getState) => {
    const { bookFragment, ssr } = getState();
    const { book } = bookFragment;
    if (!book) {
      return;
    }
    // optimistically update the UI
    dispatch(setData({
      fragment: null,
      pageNumber: page,
    }));
    const url = `/api/books/${book.id}/fragment/?fragment_page=${page}`;
    return await fetchQuery(url, { headers: ssr.headers })
      .then(
        ({ data }) => {
          dispatch(setData({
            book,
            fragment: data.fragment,
            pageNumber: data.page,
            lastPageNumber: data.last_page_number,
          }));
        }
      )
      .catch(
        ({ data, resp, error }) => {
          if (resp.status === 404) {
            dispatch(Error404Actions.show());
          }
          else {
            Raven.captureMessage('failed to load fragment content due to server error', {
              level: 'error',
              extra: {
                status: resp.status,
                response: data,
                error,
              },
            });
            throw new Error('failed to load fragment content detail due to server error');
          }
        }
      );
  };
}

export function setSeoMeta() {
  return (dispatch, getState) => {
    const { bookFragment } = getState();
    const { book: bookObject, pageNumber } = bookFragment;
    const book = new Book(bookObject);
    const cover200x300 = cropStorageImage(book.default_cover, 200, 300);
    const bookDesktopUrl = `https://${BASE_HOST}${book.getFragmentUrl()}`;
    const title = `Читать книгу «${book.name}» онлайн полностью${!book.subscription_id ? ' бесплатно' : ''} ` +
                  `${book.main_author ? `— ${book.main_author.cover_name}` : '' } ` +
                  `— MyBook. ${getPageNumberString(pageNumber)}`.trim();
    const description = truncateChars(
      `Читать онлайн книгу «${book.name}»` +
      `${book.main_author ? ` автора ${book.main_author.cover_name}` : '' }` +
      `. Простая регистрация на сайте.` +
      `${book.annotation_plain ? ` ${book.annotation_plain}` : ''}`,
      300
    );
    const ogTitle = `Читать книгу «${book.name}» онлайн` +
                    `${book.main_author ? `— ${book.main_author.cover_name}` : '' } ` +
                   `— Страница ${pageNumber} — MyBook`;
    const ogDescription = truncateChars(
      `Читать онлайн книгу «${book.name}»` +
      `${book.main_author ? ` автора ${book.main_author.cover_name}` : '' }` +
      `, страница ${pageNumber}.` +
      `${book.annotation_plain ? ` ${book.annotation_plain}` : ''}`,
      300
    );
    const ogTags = [
      { name: 'type', value: 'books.book' },
      { name: 'image', value: cover200x300 },
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'url', value: bookDesktopUrl },
    ];
    const ogTwitterTags = [
      { name: 'title', value: ogTitle },
      { name: 'description', value: ogDescription },
      { name: 'image', value: cover200x300 },
    ];
    let ogBookTags = [
      { name: 'rating:value', value: book.rating.rating ? book.rating.rating : 0 },
      { name: 'rating:scale', value: 5 },
    ];
    if (book.ean) {
      ogBookTags.push({
        name: 'isbn',
        value: book.ean,
      });
    }
    if (book.main_author) {
      ogBookTags.push({
        name: 'author',
        value: `https://${BASE_HOST}${book.main_author.absolute_url}`,
      });
    }
    dispatch(SeoActions.setData({
      imageSrc: cover200x300,
      canonicalUrl: bookDesktopUrl,
      title,
      description,
      ogTags,
      ogBookTags,
      ogTwitterTags,
      pageNumber,
    }));
  };
}

export function setData(payload) {
  return (dispatch) => {
    const { fragment } = payload;
    dispatch({
      type: BOOKS_BOOK_FRAGMENT_SET_DATA,
      payload,
    });
    if (fragment) {
      dispatch(setSeoMeta());
    }
  };
}

export function resetData() {
  return (dispatch) => {
    dispatch(SeoActions.resetData());
    dispatch({
      type: BOOKS_BOOK_FRAGMENT_RESET_DATA,
    });
  };
}
